import React, { useState } from 'react'
import { Link as RouterLink, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Box, TextField, Button, Alert, CircularProgress, Link, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { pendingInviteRoute, takePendingInvite } from '@/lib/pendingInvite'
import { useAuth } from '@/contexts/AuthContext'
import AuthShell from '@/components/AuthShell'
import { resolveApiErrorMessage } from '@/utils/apiErrorMessage'
import type { LoginCredentials } from '@/types/auth'

export default function LoginPage() {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    login: '',
    password: '',
  })
  const [error, setError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('auth')
  const { t: tErrors } = useTranslation('errors')

  const from = location.state?.from?.pathname || '/'

  // Already authenticated (e.g. VITE_AUTH_BYPASS) — don't show the login form.
  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      await login(credentials)
      // Somebody mid-way through accepting a team invitation goes back to it,
      // not to the dashboard.
      const invite = takePendingInvite()
      navigate(invite ? pendingInviteRoute(invite) : from, { replace: true })
    } catch (err) {
      setError(resolveApiErrorMessage(err, tErrors))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell title={t('signIn')} subtitle={t('signInSubtitle')}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="login"
          label={t('emailAddress')}
          name="login"
          autoComplete="email"
          autoFocus
          value={credentials.login}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t('password')}
          type="password"
          id="password"
          autoComplete="current-password"
          value={credentials.password}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Link component={RouterLink} to="/send-reset-link" variant="body2" underline="hover">
            {t('forgotPassword')}
          </Link>
        </Box>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 2.5, mb: 2, py: 1.25 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('signIn')}
        </Button>
        <Typography variant="body2" align="center" color="text.secondary">
          {t('noAccount')}{' '}
          <Link component={RouterLink} to="/register" underline="hover" fontWeight={600}>
            {t('signUpHere')}
          </Link>
        </Typography>
      </Box>
    </AuthShell>
  )
}
